import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';

// Auth Pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Pricing from './pages/Auth/Pricing';
import PlanExpired from './pages/Auth/PlanExpired';
import ForgotPassword from './pages/Auth/ForgotPassword';
import VerifyOtp from './pages/Auth/VerifyOtp';
import ResetPassword from './pages/Auth/ResetPassword';
import LandingPage from './pages/Home/LandingPage';
import FeaturesPage from './pages/Home/FeaturesPage';
import AboutUsPage from './pages/Home/AboutUsPage';
import TrialBanner from './components/TrialBanner';

// Legal Pages
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import TermsAndConditions from './pages/Legal/TermsAndConditions';
import PaymentPolicy from './pages/Legal/PaymentPolicy';
import CookiePolicy from './pages/Legal/CookiePolicy';

// Support Pages
import Contact from './pages/Support/Contact';
import BugReport from './pages/Support/BugReport';

// Global Cookie Consent
import CookieConsent from './components/CookieConsent/CookieConsent';

// Common Pages
import AddLead from './pages/Common/AddLead';
import LeadDetail from './pages/Common/LeadDetail';

// Super Admin Pages
import AdminDashboard from './pages/SuperAdmin/Dashboard';
import AdminLeads from './pages/SuperAdmin/Leads';
import AdminEmployees from './pages/SuperAdmin/Employees';
import AddEmployee from './pages/SuperAdmin/AddEmployee';
import LeadSettings from './pages/SuperAdmin/LeadSettings';
import AdminReports from './pages/SuperAdmin/Reports';
import AdminNotifications from './pages/SuperAdmin/Notifications';
import PricingPlans from './pages/SuperAdmin/PricingPlans';
import WhatsApp from './pages/SuperAdmin/WhatsApp';

// Manager Pages
import ManagerDashboard from './pages/Manager/Dashboard';
import ManagerLeads from './pages/Manager/Leads';
import ManagerTeam from './pages/Manager/Team';
import ManagerReports from './pages/Manager/Reports';
import ManagerNotifications from './pages/Manager/Notifications';
import ManagerWhatsApp from './pages/Manager/WhatsApp';

// Executive Pages
import ExecutiveDashboard from './pages/Executive/Dashboard';
import ExecutiveLeads from './pages/Executive/Leads';
import ExecutiveNotifications from './pages/Executive/Notifications';
import ExecutiveWhatsApp from './pages/Executive/WhatsApp';

// Telecaller Pages
import TelecallerDashboard from './pages/Telecaller/Dashboard';
import TelecallerLeads from './pages/Telecaller/Leads';
import TelecallerNotifications from './pages/Telecaller/Notifications';
import TelecallerWhatsApp from './pages/Telecaller/WhatsApp';

// Site Visit Pages
import SiteVisitDashboard from './pages/SiteVisitAgent/Dashboard';
import SiteVisitLeads from './pages/SiteVisitAgent/Leads';
import SiteVisitNotifications from './pages/SiteVisitAgent/Notifications';
import MasterAdminRoutes from './master-admin/MasterAdminRoutes';

// WhatsApp Web Isolation Feature Pages
import WhatsAppWebAdmin from './pages/WhatsAppWeb/SuperAdmin/WhatsAppWeb';
import WhatsAppWebManager from './pages/WhatsAppWeb/Manager/WhatsAppWebManager';
import WhatsAppWebExecutive from './pages/WhatsAppWeb/Executive/WhatsAppWebExecutive';
import WhatsAppWebTelecaller from './pages/WhatsAppWeb/Telecaller/WhatsAppWebTelecaller';

// Lead Scraper Feature Pages
import LeadScraperAdmin from './pages/LeadScrape/SuperAdmin/LeadScraper';
import LeadScraperManager from './pages/LeadScrape/Manager/LeadScraperManager';

// Automation Feature Pages
import AutomationHub from './pages/Automation/AutomationHub';
import BulkMessagePage from './pages/Automation/WhatsAppAPI/BulkMessage/BulkMessagePage';
import PendingQueue from './pages/Automation/WhatsAppAPI/BulkMessage/PendingQueue';
import AIAutoReplyPage from './pages/Automation/WhatsAppAPI/AIAutoReply/AIAutoReplyPage';

// Email Automation Pages
import EmailCampaignWizard from './pages/Automation/Email/EmailCampaignWizard';
import EmailCampaignHistory from './pages/Automation/Email/EmailCampaignHistory';
import EmailCampaignDetail from './pages/Automation/Email/EmailCampaignDetail';
import EmailTemplateManager from './pages/Automation/Email/EmailTemplateManager';

// WA Web Bulk Automation Pages
import WWebBulkHome from './pages/Automation/WhatsAppWeb/WWebBulkHome';
import WWebBulkCampaignWizard from './pages/Automation/WhatsAppWeb/WWebBulkCampaignWizard';
import WWebBulkJobsList from './pages/Automation/WhatsAppWeb/WWebBulkJobsList';
import WWebBulkJobDetail from './pages/Automation/WhatsAppWeb/WWebBulkJobDetail';
import WWebTemplateManager from './pages/Automation/WhatsAppWeb/WWebTemplateManager';

// Media Library
import MediaLibraryPage from './pages/MediaLibrary/MediaLibraryPage';

// Email Inbox (per-user two-way email client)
import EmailInboxPage from './pages/EmailInbox/EmailInboxPage';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

// Guard that shows the TrialBanner expired overlay on every super-admin page
// when the subscription is expired — not just on the dashboard.
const ExpiredSubscriptionGuard = ({ children }) => {
  const { isTrialExpired, user } = useAuth();
  // Only gate super-admin routes
  if (user?.role === 'super-admin' && isTrialExpired) {
    return <TrialBanner />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
      <Router>
        <CookieConsent />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/register" element={<Register />} />
          <Route path="/plan-expired" element={<PlanExpired />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Legal Pages (Public) */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/payment-policy" element={<PaymentPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/contact" element={<Contact />} />

          {/* Shared Protected Routes */}
          <Route path="/bug-report" element={<ProtectedRoute><BugReport /></ProtectedRoute>} />
          <Route path="/add-lead" element={<ProtectedRoute><AddLead /></ProtectedRoute>} />
          <Route path="/lead/:id" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />

          {/* Automation Routes */}
          <Route path="/automation" element={<ProtectedRoute><ExpiredSubscriptionGuard><AutomationHub /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/automation/email/send" element={<ProtectedRoute><ExpiredSubscriptionGuard><EmailCampaignWizard /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/automation/email/history" element={<ProtectedRoute><ExpiredSubscriptionGuard><EmailCampaignHistory /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/automation/email/history/:id" element={<ProtectedRoute><ExpiredSubscriptionGuard><EmailCampaignDetail /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/automation/email/templates" element={<ProtectedRoute><ExpiredSubscriptionGuard><EmailTemplateManager /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          
          {/* WA API Bulk Routes */}
          <Route path="/automation/whatsapp-api/bulk" element={<ProtectedRoute><ExpiredSubscriptionGuard><BulkMessagePage /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/automation/whatsapp-api/bulk/pending" element={<ProtectedRoute><ExpiredSubscriptionGuard><PendingQueue /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/automation/whatsapp-api/ai-reply" element={<ProtectedRoute><ExpiredSubscriptionGuard><AIAutoReplyPage /></ExpiredSubscriptionGuard></ProtectedRoute>} />

          {/* WA Web Bulk Routes */}
          <Route path="/automation/whatsapp-web/bulk" element={<ProtectedRoute><ExpiredSubscriptionGuard><WWebBulkHome /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/automation/whatsapp-web/bulk/new" element={<ProtectedRoute><ExpiredSubscriptionGuard><WWebBulkCampaignWizard /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/automation/whatsapp-web/bulk/jobs" element={<ProtectedRoute><ExpiredSubscriptionGuard><WWebBulkJobsList /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/automation/whatsapp-web/bulk/jobs/:id" element={<ProtectedRoute><ExpiredSubscriptionGuard><WWebBulkJobDetail /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/automation/whatsapp-web/templates" element={<ProtectedRoute><ExpiredSubscriptionGuard><WWebTemplateManager /></ExpiredSubscriptionGuard></ProtectedRoute>} />

          {/* Media Library */}
          <Route path="/media-library" element={<ProtectedRoute><ExpiredSubscriptionGuard><MediaLibraryPage /></ExpiredSubscriptionGuard></ProtectedRoute>} />

          {/* Email Inbox — per-user isolated email client */}
          <Route path="/email-inbox" element={<ProtectedRoute allowedRoles={['super-admin','manager','executive','telecaller']}><ExpiredSubscriptionGuard><EmailInboxPage /></ExpiredSubscriptionGuard></ProtectedRoute>} />

          {/* Super Admin Routes — wrapped with ExpiredSubscriptionGuard */}
          <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={['super-admin']}><ExpiredSubscriptionGuard><AdminDashboard /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/admin/leads" element={<ProtectedRoute allowedRoles={['super-admin']}><ExpiredSubscriptionGuard><AdminLeads /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/admin/employees" element={<ProtectedRoute allowedRoles={['super-admin']}><ExpiredSubscriptionGuard><AdminEmployees /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/admin/add-employee" element={<ProtectedRoute allowedRoles={['super-admin']}><ExpiredSubscriptionGuard><AddEmployee /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/admin/lead-sources" element={<ProtectedRoute allowedRoles={['super-admin', 'manager']}><ExpiredSubscriptionGuard><LeadSettings /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={['super-admin']}><ExpiredSubscriptionGuard><AdminReports /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/admin/notifications" element={<ProtectedRoute allowedRoles={['super-admin']}><ExpiredSubscriptionGuard><AdminNotifications /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/admin/whatsapp" element={<ProtectedRoute allowedRoles={['super-admin']}><ExpiredSubscriptionGuard><WhatsApp /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/admin/wa-web" element={<ProtectedRoute allowedRoles={['super-admin']}><ExpiredSubscriptionGuard><WhatsAppWebAdmin /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/admin/lead-scraper" element={<ProtectedRoute allowedRoles={['super-admin']}><ExpiredSubscriptionGuard><LeadScraperAdmin /></ExpiredSubscriptionGuard></ProtectedRoute>} />
          <Route path="/admin/pricing" element={<ProtectedRoute allowedRoles={['super-admin']}><PricingPlans /></ProtectedRoute>} />

          {/* Manager Routes */}
          <Route path="/manager/dashboard" element={<ProtectedRoute allowedRoles={['manager']}><ManagerDashboard /></ProtectedRoute>} />
          <Route path="/manager/leads" element={<ProtectedRoute allowedRoles={['manager']}><ManagerLeads /></ProtectedRoute>} />
          <Route path="/manager/team" element={<ProtectedRoute allowedRoles={['manager']}><ManagerTeam /></ProtectedRoute>} />
          <Route path="/manager/reports" element={<ProtectedRoute allowedRoles={['manager']}><ManagerReports /></ProtectedRoute>} />
          <Route path="/manager/notifications" element={<ProtectedRoute allowedRoles={['manager']}><ManagerNotifications /></ProtectedRoute>} />
          <Route path="/manager/whatsapp" element={<ProtectedRoute allowedRoles={['manager']}><ManagerWhatsApp /></ProtectedRoute>} />
          <Route path="/manager/wa-web" element={<ProtectedRoute allowedRoles={['manager']}><WhatsAppWebManager /></ProtectedRoute>} />
          <Route path="/manager/lead-scraper" element={<ProtectedRoute allowedRoles={['manager']}><LeadScraperManager /></ProtectedRoute>} />

          {/* Executive Routes */}
          <Route path="/executive/dashboard" element={<ProtectedRoute allowedRoles={['executive']}><ExecutiveDashboard /></ProtectedRoute>} />
          <Route path="/executive/leads" element={<ProtectedRoute allowedRoles={['executive']}><ExecutiveLeads /></ProtectedRoute>} />
          <Route path="/executive/reports" element={<ProtectedRoute allowedRoles={['executive']}><AdminReports /></ProtectedRoute>} />
          <Route path="/executive/notifications" element={<ProtectedRoute allowedRoles={['executive']}><ExecutiveNotifications /></ProtectedRoute>} />
          <Route path="/executive/whatsapp" element={<ProtectedRoute allowedRoles={['executive']}><ExecutiveWhatsApp /></ProtectedRoute>} />
          <Route path="/executive/wa-web" element={<ProtectedRoute allowedRoles={['executive']}><WhatsAppWebExecutive /></ProtectedRoute>} />

          {/* Telecaller Routes */}
          <Route path="/telecaller/dashboard" element={<ProtectedRoute allowedRoles={['telecaller']}><TelecallerDashboard /></ProtectedRoute>} />
          <Route path="/telecaller/leads" element={<ProtectedRoute allowedRoles={['telecaller']}><TelecallerLeads /></ProtectedRoute>} />
          <Route path="/telecaller/reports" element={<ProtectedRoute allowedRoles={['telecaller']}><AdminReports /></ProtectedRoute>} />
          <Route path="/telecaller/notifications" element={<ProtectedRoute allowedRoles={['telecaller']}><TelecallerNotifications /></ProtectedRoute>} />
          <Route path="/telecaller/whatsapp" element={<ProtectedRoute allowedRoles={['telecaller']}><TelecallerWhatsApp /></ProtectedRoute>} />
          <Route path="/telecaller/wa-web" element={<ProtectedRoute allowedRoles={['telecaller']}><WhatsAppWebTelecaller /></ProtectedRoute>} />

          {/* Site Visit Routes */}
          <Route path="/site-visit/dashboard" element={<ProtectedRoute allowedRoles={['site-visit-agent']}><SiteVisitDashboard /></ProtectedRoute>} />
          <Route path="/site-visit/leads" element={<ProtectedRoute allowedRoles={['site-visit-agent']}><SiteVisitLeads /></ProtectedRoute>} />
          <Route path="/site-visit/reports" element={<ProtectedRoute allowedRoles={['site-visit-agent']}><AdminReports /></ProtectedRoute>} />
          <Route path="/site-visit/notifications" element={<ProtectedRoute allowedRoles={['site-visit-agent']}><SiteVisitNotifications /></ProtectedRoute>} />

          {/* Master Admin Routes */}
          <Route path="/master-admin/*" element={<ProtectedRoute allowedRoles={['master-admin']}><MasterAdminRoutes /></ProtectedRoute>} />

          {/* Root Redirection Logic */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}



const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <LandingPage />;
  
  const roleRoutes = {
    'master-admin': '/master-admin',
    'super-admin': '/admin/dashboard',
    'manager': '/manager/dashboard',
    'executive': '/executive/dashboard',
    'telecaller': '/telecaller/dashboard',
    'site-visit-agent': '/site-visit/dashboard'
  };
  
  return <Navigate to={roleRoutes[user.role] || '/login'} replace />;
};

export default App;



